import Button from "@/types/components/Button.interface"
import IListAction, { IListActionType } from "@/types/components/IListAction.interface"
import IListIcon, { IListIconType } from "@/types/components/IListIcon.interface"
import IListTile from "@/types/components/IListTile.interface"
import Media, { MediaFolder } from "@/types/Media.interface"
import Person from "@/types/Person.interface"
import { popPreview } from '@/utility/helpers'
interface FileValidateResponse {
    validFiles: File[]
    invalidFiles: InvalidFile[]
}
interface InvalidFile {
    file: File
    error: string
}

export default {
    data () {
        return {
            filter: {
                selected: {
                    display: 'Newest to Oldest',
                    key: 'sort',
                    value: 'desc'
                },
                options: [
                    {
                        display: 'Newest to Oldest',
                        key: 'sort',
                        value: 'desc'
                    },
                    {
                        display: 'Oldest to Newest',
                        key: 'sort',
                        value: 'asc'
                    },
                    // {
                    //     display: 'Largest to Smallest',
                    //     key: 'filter',
                    //     value: 'large'
                    // },
                    // {
                    //     display: 'Smallest to Largest',
                    //     key: 'filter',
                    //     value: 'small'
                    // },
                    {
                        display: 'Images Only',
                        key: 'filter',
                        value: 'images'
                    },
                    {
                        display: 'PDFs Only',
                        key: 'filter',
                        value: 'pdfs'
                    }
                ]
            }
        }
    },
    computed: {
        approachingLimit(): boolean {
            //  Approaching limit is when you have 10% or less of your storage remaining
            //  Formula is (limit - used) being <= 10% of limit
            return this.storageLimit - this.storageUsed <= this.storageLimit * 0.1
        },
        operator(): boolean {
            //  Grab our current role
            const role = this.$store.getters.userRole(this.$store.getters.user)

            //  Check if this role is an operator
            return window.getProps(role, ['name']) === 'Operator'
        },
        storageLimit(): number {
            return this.$store.getters.storageLimit
        },
        storageUsed(): number {
            return this.$store.getters.storageUsed
        },
        validFileTypes(): string[] {
            const validFileTypes = ['application/pdf', 'image/gif', 'image/jpeg', 'image/jpg', 'image/png', 'text/plain', 'text/csv']
            return validFileTypes
        },
        validSizeLimit(): number {
            const validSizeLimit = 12000000 // 12000000 Bytes = 12MB
            return validSizeLimit
        },
        validNameLength(): number {
            const validNameLength = 120
            return validNameLength
        }
    },
    methods: {
        async filesChange(fileList: FileList): Promise<Media> {
            //  Turn our FileList into an array so we can use it
            const files = Array.from(fileList)

            //  Validate our files to only get valid ones
            const filtered = this.validateFiles(files)

            //  If we have any invalid files then we should warn the user
            if (filtered.invalidFiles.length > 0) {
                this.$store.dispatch('dialogOpen', {
                    type: 'MESSAGE',
                    title: 'Invalid Files',
                    icon: 'mdi-alert-circle',
                    text: this.getInvalidMessage(filtered.invalidFiles)
                })
            }

            //  Else, upload these files and return the uploaded file
            return await this.uploadFiles(filtered.validFiles, this.person, this.folder)
        },
        getInvalidMessage(invalidFiles: InvalidFile[]): string {
            //  Our message default
            let message = 'The following files failed to upload:<br>'

            //  Concatenate each error message to our default error message
            invalidFiles.forEach((item: InvalidFile) => {
                message += `${item.file.name}: ${item.error}<br>`
            })

            //  Return the fully concatenated error message
            return message
        },
        goToMediaUpload(person: Person = null, folder: MediaFolder = null): void {
            //  Block the user if they have used too much storage
            if (this.approachingLimit) return this.popLimitWarning()

            //  User need to upload a document for this person, pop a form
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'media-form',
                closeable: true,
                closeOnSubmit: true,
                props: {
                    folder: folder,
                    person: person
                }
            })
        },
        mediaActions(media: Media): IListAction {
            //  Else, create our menu of buttons
            const menu: Button[] = [
                {
                    icon: 'bi-download',
                    text: 'Download File',
                    color: 'primary',
                    callback: () => this.mediaDownload(media),
                    disabledPopup: this.operator
                },
                {
                    icon: 'mdi-pencil',
                    text: 'Edit File Name',
                    color: 'primary',
                    callback: () => this.mediaEdit(media),
                    disabledPopup: this.operator
                },
                {
                    icon: 'mdi-arrow-top-right-thick',
                    text: 'View Profile',
                    color: 'primary',
                    callback: () => this.mediaViewProfile(media),
                    disabledPopup: this.operator,
                    hidden: !media.person_id
                },
                {
                    icon: 'bi-trash-fill',
                    text: 'Delete File',
                    color: 'primary',
                    callback: () => this.mediaDeleteConfirm(media),
                    disabledPopup: this.operator
                },
            ]

            //  Create our IListAction
            const action: IListAction = {
                type: IListActionType.menu,
                menu: menu
            }

            //  Return our list action
            return action
        },
        mediaDelete(dialog_result: boolean, media: Media): void {
            //  If the user answered 'NO' in the dialog, do nothing
            if (!dialog_result) return

            //  Else, remove the 'favorite' flag from the document
            media.favorite = false

            //  Delete the document
            this.$store.dispatch('mediaDelete', media)
        },
        mediaDeleteConfirm(media: Media): void {
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                title: 'Delete File?',
                text: `Are you sure you want to delete <b>${media.name}</b>?`,
                icon: 'mdi-alert',
                color: 'yellow darken-2',
                callback: (result: boolean): void => this.mediaDelete(result, media)
            })
        },
        mediaDownload(media: Media): void {
            this.$store.dispatch('mediaDownload', media)
        },
        mediaEdit(media: Media): void {
            //  Select the desired form
            const form = {
                model: media,
                inputs: [
                    {
                        type: 'TEXT',
                        name: 'File Name',
                        label: 'File Name',
                        path: 'name',
                        validate: 'max:45'
                    },
                ]
            }

            //  Pop the app-form dialog
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                title: 'Edit File Name',
                icon: 'mdi-pencil',
                component: 'app-form',
                props: form,
                closeable: true,
                callback: async (result: Media): Promise<void> => {
                    //  Update our media
                    const response = await this.$store.dispatch('mediaPut', result)

                    //  Update our media on the client
                    media.name = response.name

                    //  Close our popup
                    this.$store.dispatch('dialogClose', 'APP')
                }
            })
        },
        mediaIcon(media: Media): IListIcon {
            //  Get our icon based on the type of media
            const icon = this.$options.filters.mediaIcon(media)

            //  Create our list icon
            const listIcon: IListIcon = {
                type: IListIconType.icon,
                content: icon,
                color: 'primary'
            }

            //  Return our created icon
            return listIcon
        },
        mediaViewProfile(media: Media): void {
            //  Go to the profile on this media
            this.$router.push(`/contacts?id=${media.person_id}`)
        },
        async onDrop(event: DragEvent): Promise<Media> {
            //  Prevent default popup
            event.preventDefault()

            //  IF there are no items, do nothing
            if (!event.dataTransfer.items) return

            //  Else, look for our file
            let file = null

            //  Iterate for our file
            for (let i = 0; i < event.dataTransfer.items.length; i++) {
                //  Ref to our current item
                let curr = event.dataTransfer.items[i]

                //  If the current is NOT a file, skip
                if (curr.kind !== 'file') continue

                //  Else, this is our file, and break
                file = curr.getAsFile()
                break
            }

            //  If no file is found, do nothing
            if (!file) return

            //  Start uploading
            this.uploading = true

            //  Upload our image
            const img = await this.filesChange([file])

            //  Stop uploading
            this.uploading = false

            //  Return our image
            return img
        },
        popLimitWarning(): void {
            //  Get our storage limit
            const size = this.$options.filters.fileSize(this.storageLimit)

            //  Pop a dialog warning the user
            this.$store.dispatch('dialogOpen', {
                type: this.$route.path.includes('inbox') ? 'CONFIRM' : 'MESSAGE' ,
                title: 'Storage Limit Reached',
                text: `You have reached your storage limit of ${size}. Please delete unused files before continuing to upload media.`,
                closeable: true,
                link: this.$route.path.includes('inbox') ? '/media' : null
            })
        },
        mediaTile(): IListTile {
            return {
                leftIcon: (m: Media): IListIcon => this.mediaIcon(m),
                leftHeader: (m: Media): string => this.$options.filters.mediaIconText(m),
                line1: (m: Media): string => m.name,
                line2: (m: Media): string => `Uploaded ${this.$options.filters.dateReadable(m.created_at)}`,
                actionHeader: (m: Media): string => this.$options.filters.fileSize(m.size),
                actionContent: (m: Media): IListAction => this.mediaActions(m),
                callback: (m: Media): void => popPreview(m),
            }
        },
        async uploadFile(file: File, person: Person = null, folder: MediaFolder = null): Promise<Media> {
            //  Assemble our formdata for the request
            const formData = new FormData()

            //  Attach the file
            formData.append('file', file)

            //  If person is included, append to the request
            if (person) formData.append('person_id', this.person.id)

            //  If folder is included, append to the request
            formData.append('folder', 'uploaded')

            //  Wait for our post to finish uploading
            const res = await this.$store.dispatch('mediaPost', formData)

            //  Return our result
            return res
        },
        async uploadFiles(filtered: File[], person: Person = null, folder: MediaFolder = null): Promise<Media> {
            //  Container for our media
            let media = null

            //  If we only have 1 item in the list, we will do it async await
            if (filtered.length === 1) {
                //  Wait for media to finish uploading
                media = await this.uploadFile(filtered[0], person, folder)
            }
            else {
                //  Else we have an array of media so upload them all async
                await Promise.all(filtered.map(async (file) => {
                    await this.uploadFile(file, person, folder)
                }))
            }

            //  Check if we are in the inbox
            const inbox = this.$route.path.includes('inbox')

            //  Emit the submit event to close the dialog
            if (!inbox) this.$emit('submit')

            //  Return our media
            return media
        },
        validateFiles(files: File[]): FileValidateResponse {
            //  Containers for our valid and invalid files
            const validFiles = []
            const invalidFiles = []

            files.forEach((file) => {
                if (file.size > this.validSizeLimit) {
                    invalidFiles.push({ file: file, error: 'File size too large' })
                }
                else if (!this.validFileTypes.includes(file.type)) {
                    invalidFiles.push({ file: file, error: 'Invalid file type' })
                }
                else if (file.name.length > this.validNameLength) {
                    invalidFiles.push({ file: file, error: 'File name too long' })
                }
                else if (file.name.match(/<\/?[^>]+(>|$)/g)) {
                    invalidFiles.push({ file: file, error: 'Invalid file name' })
                }
                else validFiles.push(file)
            })

            return {
                validFiles: validFiles,
                invalidFiles: invalidFiles
            }
        }
    }
}
